import { QURAN_CREDIT } from '@iqrapath/shared'
import { ChevronLeft, HeartHandshake, Info, Send } from 'lucide-react'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { EmptyState } from '../../components/EmptyState.tsx'
import { Obj } from '../../components/Obj.tsx'
import { SkCard } from '../../components/Skeleton.tsx'
import { SourceLink } from '../../components/SourceLink.tsx'
import { ApiError } from '../../lib/api.ts'
import { shareText } from '../../lib/share.ts'
import { JadikanNiat } from '../perjalanan/parts.tsx'
import { PassageBlock } from './PassageBlock.tsx'
import { RASA_INFO, tintOf, usePegangan, type PeganganCard } from './pegangan.ts'

const STEPS = [['sekarang', 'Sekarang'], ['hariIni', 'Hari ini'], ['mingguIni', 'Minggu ini']] as const

function CardView({ c }: { c: PeganganCard }) {
  const [shared, setShared] = useState('')
  const info = RASA_INFO[c.rasa]
  const share = async () => {
    const quote = c.ayat.verses ? `${c.ayat.verses.map((v) => v.terjemah).join(' ')}\n(${c.ayat.ref})` : c.ayat.ref
    const r = await shareText(c.judul, `${c.judul}\n\n${quote}\n\n${c.langkah.sekarang}\n\nDibagikan dari IqraPath`)
    setShared(r === 'whatsapp' ? 'WhatsApp dibuka di tab baru.' : r === 'shared' ? 'Terkirim. Semoga jadi pegangan juga untuknya.' : '')
  }

  return (
    <article className="pg">
      <header className="pg-hero" style={{ background: tintOf(c.rasa) }}>
        <Link className="back" to={`/pegangan?rasa=${c.rasa}`}><ChevronLeft size={18} aria-hidden="true" /> Pegangan</Link>
        <div className="pg-hero-in">
          <div>
            <span className="pill pg-rasa">{info.label}</span>
            <h1>{c.judul}</h1>
            <p className="pg-ringkas">{c.ringkas}</p>
          </div>
          <Obj id={info.obj} size={96} className="empty-obj" />
        </div>
      </header>

      <div className="page pg-body">
        {!c.reviewed && (
          <p className="pg-draft" role="note">
            <Info size={16} aria-hidden="true" />{' '}
            {c.ai ? 'Draf AI: disusun dengan bantuan AI dan masih menunggu tinjauan ustadz.' : 'Draf: isi kartu ini masih menunggu tinjauan ustadz.'}
          </p>
        )}

        <section className="card pg-sec pg-ayat" aria-labelledby="pg-ayat-h">
          <h2 id="pg-ayat-h" className="section-t">Ayat pegangan</h2>
          <PassageBlock p={c.ayat} id={`pg-${c.id}-ayat`} what="ayat pegangan" />
        </section>

        <section className="card pg-sec" aria-labelledby="pg-makna-h">
          <h2 id="pg-makna-h">Maknanya untukmu</h2>
          <p>{c.makna}</p>
        </section>

        <p className="pg-teman"><HeartHandshake size={20} aria-hidden="true" /> <span><b>Kamu nggak sendirian.</b> {c.temanmu}</span></p>

        {c.teladan && (
          <section className="card pg-sec" aria-labelledby="pg-teladan-h">
            <h2 id="pg-teladan-h">Teladan</h2>
            <p>{c.teladan.teks}</p>
            <p className="pg-src"><SourceLink href={c.teladan.url}>{c.teladan.ref}</SourceLink></p>
          </section>
        )}

        {c.hadis.length > 0 && (
          <section className="card pg-sec" aria-labelledby="pg-hadis-h">
            <h2 id="pg-hadis-h">Hadis</h2>
            {c.hadis.map((h) => (
              <figure key={h.id} className="pg-hadis">
                <span className="pill pill-mint">{h.grade}</span>
                {/* verbatim from HadeethEnc (their terms: no changes) */}
                <blockquote>{h.text}</blockquote>
                <figcaption>
                  <span>{h.attribution}</span>
                  <SourceLink href={h.url}>{h.source}</SourceLink>
                </figcaption>
              </figure>
            ))}
          </section>
        )}

        <section className="card pg-sec" aria-labelledby="pg-langkah-h">
          <h2 id="pg-langkah-h">Tiga langkah kecil</h2>
          <ol className="pg-steps">
            {STEPS.map(([k, label]) => (
              <li key={k}><span className="pg-when">{label}</span>{c.langkah[k]} <JadikanNiat text={c.langkah[k]} source="pegangan" sourceRef={c.id} /></li>
            ))}
          </ol>
        </section>

        {c.doa && (
          <section className="card pg-sec pg-ayat" aria-labelledby="pg-doa-h">
            <h2 id="pg-doa-h" className="section-t">Doa</h2>
            <PassageBlock p={c.doa} id={`pg-${c.id}-doa`} what="doa" />
          </section>
        )}

        {c.catatan && <p className="pg-note" role="note"><Info size={16} aria-hidden="true" /> {c.catatan}</p>}

        <button type="button" className="btn btn-secondary btn-block" onClick={share} style={{ marginTop: 18 }}>
          <Send size={18} aria-hidden="true" /> Kirim ke seseorang
        </button>
        <p className="help" role="status" style={{ textAlign: 'center' }}>{shared}</p>

        <p className="credit" style={{ marginTop: 14 }}><SourceLink href={c.ayat.url}>{QURAN_CREDIT}</SourceLink>. Bahan renungan, bukan fatwa.</p>
      </div>
    </article>
  )
}

/** /pegangan/:id: one situation card (docs/PRD.md US-6.2). */
export function PeganganPage() {
  const { id = '' } = useParams()
  const { data, isPending, error, refetch } = usePegangan(id)
  if (isPending) return <div className="page"><SkCard label="Memuat kartu" ar lines={4} h={320} /></div>
  if (error) {
    const gone = error instanceof ApiError && error.status === 404
    return (
      <div className="page">
        <EmptyState
          obj={gone ? 'kado' : 'jam-pasir'}
          title={gone ? 'Kartu ini tidak ditemukan' : 'Kartu belum bisa dimuat'}
          action={gone ? <Link className="btn btn-ghost" to="/pegangan">Lihat semua kartu</Link> : <button className="btn btn-secondary" onClick={() => refetch()}>Coba lagi</button>}
        >
          {gone ? 'Mungkin sudah diganti. Masih banyak kartu lain yang bisa jadi peganganmu.' : 'Koneksi lagi bermasalah. Cek internet kamu, lalu coba lagi.'}
        </EmptyState>
      </div>
    )
  }
  return <CardView c={data} />
}
